export class AudioProcessor {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private analyser: AnalyserNode | null = null;
  /** FIFO of raw capture chunks awaiting assembly into sliding detection windows. */
  private audioBuffer: Float32Array[] = [];
  /** Samples currently held in `audioBuffer` (sum of chunk lengths). */
  private bufferedSamples = 0;
  /** Absolute sample index (since startCapture) of the first sample in `audioBuffer`. */
  private consumedSamples = 0;
  /** Wall-clock time (epoch ms) of the first captured sample — anchor for window timestamps. */
  private captureStartEpochMs = 0;
  private readonly sampleRate = 16000;
  private readonly windowSize = 48000; // 3 s @ 16 kHz
  private readonly hopSize = 16000; // 1 s hop → 2 s overlap between consecutive windows
  /** Public URL of the AudioWorklet module (served from /public, absolute path). */
  private readonly workletUrl = "/audio-capture-processor.js";
  // Multiple subscribers: the detector and an optional spectrogram/visualiser can both listen
  // to the same audio without clobbering each other (the old single-setter overwrote them).
  // Each window carries the epoch-ms timestamp of its FIRST sample, derived from the sample
  // counter (not Date.now() at emit time) so it is immune to inference jitter — TDOA needs this.
  private onSpectrogramCallbacks: ((spectrogram: Float32Array, windowStartMs: number) => void)[] = [];
  private onAudioCallbacks: ((audio: Float32Array) => void)[] = [];
  private isProcessing = false;
  /** Reused scratch buffer for getCurrentRMS() — polled ~10×/s, avoids per-call allocation. */
  private rmsBuffer: Float32Array<ArrayBuffer> | null = null;

  // ── Rolling raw-audio ring (for the own-events "listen back" clip) ────────────────────────
  // A fixed-size circular buffer of the most recent raw samples, retained independently of the
  // sliding detection window (which drops audio as it advances). It lets the radar extract a
  // 9-second clip (3 s before … 3 s after a detection) so the user can listen to what fired.
  // PRIVACY: this never leaves the device — it is only read locally for in-app playback.
  /** ~13 s @ 16 kHz — enough for a 9 s clip plus the ~3 s "after-audio" wait latency. */
  private readonly rawRingCapacity = 16000 * 13;
  private readonly rawRing = new Float32Array(this.rawRingCapacity);
  /** Total samples ever written to the ring since startCapture — absolute sample clock. */
  private rawRingWritten = 0;

  async startCapture(): Promise<void> {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.sampleRate,
          channelCount: 1,
          // All browser DSP off: AGC compresses explosion transients and drifts the level,
          // which corrupts Stage-0 RMS thresholds and the model's input normalization.
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });

      // Reset sliding-window state and anchor the sample clock to now.
      this.audioBuffer = [];
      this.bufferedSamples = 0;
      this.consumedSamples = 0;
      this.captureStartEpochMs = Date.now();
      this.rawRing.fill(0);
      this.rawRingWritten = 0;

      this.audioContext = new AudioContext({
        sampleRate: this.sampleRate,
      });

      const source = this.audioContext.createMediaStreamSource(
        this.mediaStream,
      );
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;
      source.connect(this.analyser);

      // AudioWorklet replaces the deprecated ScriptProcessorNode — capture runs off the main
      // thread. The module must be loaded before the node can be instantiated.
      if (!this.audioContext.audioWorklet) {
        throw new Error("AudioWorklet не поддерживается в этом браузере");
      }
      await this.audioContext.audioWorklet.addModule(this.workletUrl);

      this.workletNode = new AudioWorkletNode(
        this.audioContext,
        "audio-capture-processor",
        { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] },
      );

      this.workletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
        this.processAudioChunk(event.data);
      };

      source.connect(this.workletNode);
      // The graph is pull-based — connect to the destination so process() is driven. The node
      // never writes its output, so the destination receives silence (no feedback).
      this.workletNode.connect(this.audioContext.destination);
      this.isProcessing = true;
    } catch (error) {
      console.error("Audio capture failed:", error);
      throw new Error("Не удалось получить доступ к микрофону");
    }
  }

  stopCapture(): void {
    if (this.workletNode) {
      this.workletNode.port.onmessage = null;
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    if (this.audioContext) {
      void this.audioContext.close();
      this.audioContext = null;
    }

    this.isProcessing = false;
    this.audioBuffer = [];
    this.bufferedSamples = 0;
    this.consumedSamples = 0;
  }

  private processAudioChunk(chunk: Float32Array): void {
    if (!this.isProcessing) return;

    this.audioBuffer.push(chunk);
    this.bufferedSamples += chunk.length;
    this.writeRawRing(chunk);

    for (const cb of this.onAudioCallbacks) cb(chunk);

    // Sliding window: emit a full window whenever enough samples have accumulated, then
    // advance by hopSize (keeping windowSize-hopSize samples of overlap). The `while` loop
    // drains any backlog if a single chunk pushed us past multiple hops at once.
    while (this.bufferedSamples >= this.windowSize) {
      const window = this.peekWindow(this.windowSize);
      // Timestamp of the window's first sample, derived from the absolute sample counter.
      const windowStartMs =
        this.captureStartEpochMs + (this.consumedSamples / this.sampleRate) * 1000;
      for (const cb of this.onSpectrogramCallbacks) cb(window, windowStartMs);
      this.dropFromFront(this.hopSize);
    }
  }

  /** Copy the first `samples` samples from the FIFO into a new array, without consuming them. */
  private peekWindow(samples: number): Float32Array {
    const result = new Float32Array(samples);
    let offset = 0;
    for (const chunk of this.audioBuffer) {
      if (offset >= samples) break;
      const remaining = samples - offset;
      if (chunk.length <= remaining) {
        result.set(chunk, offset);
        offset += chunk.length;
      } else {
        result.set(chunk.subarray(0, remaining), offset);
        offset = samples;
        break;
      }
    }
    return result;
  }

  /** Drop the first `samples` samples from the FIFO, advancing the sliding window. */
  private dropFromFront(samples: number): void {
    let toDrop = samples;
    while (toDrop > 0 && this.audioBuffer.length > 0) {
      const head = this.audioBuffer[0];
      if (head.length <= toDrop) {
        toDrop -= head.length;
        this.audioBuffer.shift();
      } else {
        this.audioBuffer[0] = head.subarray(toDrop);
        toDrop = 0;
      }
    }
    this.bufferedSamples -= samples;
    this.consumedSamples += samples;
  }

  /** Append a chunk to the rolling raw ring (circular write; oldest samples are overwritten). */
  private writeRawRing(chunk: Float32Array): void {
    const cap = this.rawRingCapacity;
    let pos = this.rawRingWritten % cap;
    for (let i = 0; i < chunk.length; i++) {
      this.rawRing[pos] = chunk[i];
      pos++;
      if (pos === cap) pos = 0;
    }
    this.rawRingWritten += chunk.length;
  }

  /**
   * Extract a clip of raw audio from the rolling ring by absolute capture-clock time.
   *
   * @param startEpochMs - wall-clock start of the clip (epoch ms), same clock onsets use.
   * @param durationMs   - clip length in ms.
   * @returns a freshly-allocated Float32Array of the requested span, or null if any part of it
   *          has already scrolled out of the ring (or capture isn't running). Out-of-range
   *          regions at the very edges are zero-padded.
   *
   * PRIVACY: the returned audio is local by default (in-app "listen back" only); it is never
   *           uploaded automatically. The user may explicitly export it (download / upload-for-
   *           analysis) from the My-Events card — that is a deliberate, manual action.
   */
  extractClip(startEpochMs: number, durationMs: number): Float32Array | null {
    if (this.rawRingWritten === 0) return null;
    const cap = this.rawRingCapacity;
    const sr = this.sampleRate;
    const startSample = Math.round(((startEpochMs - this.captureStartEpochMs) / 1000) * sr);
    const length = Math.round((durationMs / 1000) * sr);
    if (length <= 0 || length > cap) return null;

    // The ring currently holds absolute samples [oldest, rawRingWritten).
    const oldest = Math.max(0, this.rawRingWritten - cap);
    const endSample = startSample + length;
    // Bail if the clip is entirely outside what the ring still holds.
    if (endSample <= oldest || startSample >= this.rawRingWritten) return null;

    const out = new Float32Array(length);
    for (let i = 0; i < length; i++) {
      const abs = startSample + i;
      if (abs < oldest || abs >= this.rawRingWritten) continue; // edge → leave as 0 (pad)
      out[i] = this.rawRing[abs % cap];
    }
    return out;
  }

  /** Subscribe to full detection windows (48000 samples). Returns an unsubscribe function. */
  onSpectrogram(callback: (spectrogram: Float32Array, windowStartMs: number) => void): () => void {
    this.onSpectrogramCallbacks.push(callback);
    return () => {
      this.onSpectrogramCallbacks = this.onSpectrogramCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /** Subscribe to raw audio chunks as they arrive. Returns an unsubscribe function. */
  onAudio(callback: (audio: Float32Array) => void): () => void {
    this.onAudioCallbacks.push(callback);
    return () => {
      this.onAudioCallbacks = this.onAudioCallbacks.filter((cb) => cb !== callback);
    };
  }

  getCurrentRMS(): number {
    if (!this.analyser || !this.isProcessing) return 0;

    if (!this.rmsBuffer || this.rmsBuffer.length !== this.analyser.fftSize) {
      this.rmsBuffer = new Float32Array(this.analyser.fftSize);
    }
    const dataArray = this.rmsBuffer;
    this.analyser.getFloatTimeDomainData(dataArray);

    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    return Math.sqrt(sum / dataArray.length);
  }

  isCapturing(): boolean {
    return this.isProcessing;
  }
}
